/**
 * The account's agents as the V2 shell shows them (the agents strip and one thread per agent): the main agent (its
 * settings, its conversation) then the extra ones (GET /api/bots), each with its last message and an unread dot. What
 * was read is a convenience of this device (localStorage): the newest message seen in each conversation.
 */
import { useMemo, useSyncExternalStore } from "react";
import type { BotView } from "../../shared/bots";
import { chatUiFallbackText } from "../../shared/chat-ui";
import type { AgentAvatar, AgentCharacter, ChatMessage } from "../../shared/contracts";
import { api } from "./api";
import { useSettings } from "./agent";
import { useResource } from "./store";

export interface ShellAgent {
  /** "main" or the bot's id. */
  id: string;
  main: boolean;
  name: string;
  avatar: AgentAvatar | null;
  character: AgentCharacter | null;
  /** One line of its last message (empty when none). */
  last: string;
  lastAt: number;
  unread: boolean;
}

const PAGE = 50;

/** The main conversation (shared with the conversation page: same key, same request). */
export function useMainChat(pollMs: number | false = false) {
  return useResource<ChatMessage[]>("chat", async (signal) => (await api("GET /api/chat", { query: { limit: String(PAGE) }, signal })).messages, {
    refreshOn: ["turn.done"],
    pollMs,
  });
}

export function useBots() {
  return useResource("bots", (signal) => api("GET /api/bots", { signal }), { staleMs: 30_000 });
}

/** The last line of each extra agent's conversation (their events do not reach the main socket: a slow poll). */
function useBotLasts(bots: BotView[] | undefined) {
  const ids = (bots ?? []).filter((b) => !b.main).map((b) => b.id);
  const key = `bots:last:${ids.join(",")}`;
  return useResource<Record<string, ChatMessage | null>>(
    key,
    async (signal) => {
      const out: Record<string, ChatMessage | null> = {};
      await Promise.all(
        ids.map(async (id) => {
          const res = await api("GET /api/bots/:botId/chat", { params: { botId: id }, query: { limit: "5" }, signal }).catch(() => null);
          out[id] = lastVisible(res?.messages ?? []);
        }),
      );
      return out;
    },
    { enabled: ids.length > 0, pollMs: 30_000, staleMs: 15_000 },
  );
}

/** The newest line that reads as a message (not a tapback). */
export function lastVisible(messages: readonly ChatMessage[]): ChatMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) if (!messages[i]!.reaction) return messages[i]!;
  return null;
}

/** A message on one line: its text, or its interface's plain version, without markdown marks. */
export function oneLine(m: ChatMessage | null): string {
  if (!m) return "";
  const text = m.text.trim() || (m.ui ? chatUiFallbackText(m.ui) : "") || (m.files?.length ? m.files.map((f) => f.name).join(", ") : "");
  return text
    .replace(/\[\[[^\]]{0,60}\]\]/g, "")
    .replace(/[*_`#>]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const SEEN_KEY = "pulsez.seen";

function readSeen(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

/** The newest message of `agentId`'s conversation was seen on this device. */
export function markSeen(agentId: string, at: number): void {
  if (!at) return;
  try {
    const seen = readSeen();
    if ((seen[agentId] ?? 0) >= at) return;
    seen[agentId] = at;
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    window.dispatchEvent(new Event("pulsez:seen"));
  } catch {
    // Private browsing: every conversation reads as seen.
  }
}

/** The main agent then the extra ones, with their last message and an unread dot. */
export function useShellAgents(): ShellAgent[] {
  const settings = useSettings();
  const chat = useMainChat();
  const bots = useBots();
  const lasts = useBotLasts(bots.data?.bots);
  const seen = useSeenVersion();
  return useMemo(() => {
    void seen;
    const marks = readSeen();
    const s = settings.data;
    const mainLast = lastVisible(chat.data ?? []);
    const main: ShellAgent = {
      id: "main",
      main: true,
      name: s?.agentName ?? "Nova",
      avatar: s?.avatar ?? null,
      character: s?.character ?? null,
      last: oneLine(mainLast),
      lastAt: mainLast?.createdAt ?? 0,
      unread: Boolean(mainLast && mainLast.direction === "out" && mainLast.createdAt > (marks.main ?? 0)),
    };
    const extra = (bots.data?.bots ?? [])
      .filter((b) => !b.main)
      .map((b): ShellAgent => {
        const m = lasts.data?.[b.id] ?? null;
        return {
          id: b.id,
          main: false,
          name: b.name,
          avatar: b.avatar,
          character: b.character ?? null,
          last: oneLine(m),
          lastAt: m?.createdAt ?? 0,
          unread: Boolean(m && m.direction === "out" && m.createdAt > (marks[b.id] ?? 0)),
        };
      });
    return [main, ...extra];
  }, [settings.data, chat.data, bots.data, lasts.data, seen]);
}

let seenVersion = 0;
const seenListeners = new Set<() => void>();
if (typeof window !== "undefined") {
  window.addEventListener("pulsez:seen", () => {
    seenVersion += 1;
    for (const fn of [...seenListeners]) fn();
  });
}

/** Changes whenever a conversation is marked as seen (re-renders the unread dots). */
function useSeenVersion(): number {
  return useSyncExternalStore(
    (fn) => {
      seenListeners.add(fn);
      return () => seenListeners.delete(fn);
    },
    () => seenVersion,
    () => seenVersion,
  );
}
